import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { createAppTheme, DEFAULT_THEME_PRESET, isValidPreset } from './index';
import { uiThemeService } from '../services/uiThemeService';
import { useCompany } from '../context/CompanyProvider';

const LOCAL_OVERRIDE_KEY = 'wm_ui_theme_override';
const SERVER_CACHE_KEY = 'wm_ui_theme_server';

const readStored = (key) => {
  try {
    const value = window.localStorage.getItem(key);
    return isValidPreset(value) ? value : null;
  } catch {
    return null;
  }
};

const writeStored = (key, value) => {
  try {
    if (value) window.localStorage.setItem(key, value);
    else window.localStorage.removeItem(key);
  } catch {
    // Private browsing / storage disabled — theme just falls back to the server value
  }
};

const BRANDED_PRESETS = ['fabworks', 'langley', 'whw'];

const coercePresetForCompany = (preset, company) => {
  const fallback = company?.themePreset || DEFAULT_THEME_PRESET;
  if (!isValidPreset(preset)) return fallback;
  if (BRANDED_PRESETS.includes(preset) && company?.id && preset !== company.id) return fallback;
  return preset;
};

const ThemeModeContext = createContext({
  preset: DEFAULT_THEME_PRESET,
  serverPreset: DEFAULT_THEME_PRESET,
  localOverride: null,
  setLocalOverride: () => {},
  saveServerPreset: async () => {},
});

export const useThemeMode = () => useContext(ThemeModeContext);

/**
 * Resolves the active theme as: personal override (localStorage) -> company setting -> default.
 * The last known company setting is cached so the first paint never flashes the wrong theme.
 */
export const ThemeModeProvider = ({ children }) => {
  const { company } = useCompany();
  const companyPreset = company?.themePreset;
  const [serverPreset, setServerPreset] = useState(
    () => coercePresetForCompany(readStored(SERVER_CACHE_KEY), company) || DEFAULT_THEME_PRESET
  );
  const [localOverride, setLocalOverrideState] = useState(() => readStored(LOCAL_OVERRIDE_KEY));

  useEffect(() => {
    let cancelled = false;
    uiThemeService
      .get()
      .then((res) => {
        const next = coercePresetForCompany(res?.data?.preset, company);
        if (cancelled || !isValidPreset(next)) return;
        setServerPreset(next);
        writeStored(SERVER_CACHE_KEY, next);
      })
      .catch(() => {
        // Offline or endpoint unavailable — keep the cached/default preset
      });
    return () => {
      cancelled = true;
    };
  }, [company]);

  const setLocalOverride = useCallback((next) => {
    const value = isValidPreset(next) ? next : null;
    setLocalOverrideState(value);
    writeStored(LOCAL_OVERRIDE_KEY, value);
  }, []);

  const saveServerPreset = useCallback(async (next) => {
    const res = await uiThemeService.update(next);
    const saved = res?.data?.preset || next;
    setServerPreset(saved);
    writeStored(SERVER_CACHE_KEY, saved);
    return saved;
  }, []);

  const preset =
    localOverride ||
    serverPreset ||
    (isValidPreset(companyPreset) ? companyPreset : null) ||
    DEFAULT_THEME_PRESET;
  const theme = useMemo(() => createAppTheme(preset), [preset]);

  const value = useMemo(
    () => ({ preset, serverPreset, localOverride, setLocalOverride, saveServerPreset }),
    [preset, serverPreset, localOverride, setLocalOverride, saveServerPreset]
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};

export default ThemeModeProvider;
